import React, { useCallback, useEffect, useState } from 'react';
import { fetchSiteAttendance, clockSitePersonnel } from '../../apis/siteAttendanceApi';
import { ISiteAttendanceRow, SiteAttendanceAction } from '../../interface/SiteAttendance';
import { getUserData } from '../../utils/utils';

// Roles that may look but not change, matching the rest of the app.
const READ_ONLY_ROLES = [
    'Scope.AdminView',
    'Scope.StudentService',
    'Scope.PayrollService1',
    'Scope.PayrollService2',
    'Scope.StudentService_PayrollService1',
    'Scope.StudentService_PayrollService1AND2',
];

const today = () => new Date().toLocaleDateString('fr-CA'); // YYYY-MM-DD, local

// Times arrive as "HH:mm:ss"; show and edit them as "HH:mm".
const showTime = (value?: string | null): string => (value ? value.slice(0, 5) : '—');

interface SiteAttendanceModalProps {
    siteId: number;
    siteName?: string;
    onClose: () => void;
}

const SiteAttendanceModal: React.FC<SiteAttendanceModalProps> = ({ siteId, siteName, onClose }) => {
    const userData = getUserData();
    const canEdit = !READ_ONLY_ROLES.includes(userData?.role);

    // Name, position, scheduled, in, out, plus time and action when editable.
    // The loading and empty rows span the lot.
    const columnCount = canEdit ? 7 : 5;

    const [date, setDate] = useState(today());
    const [rows, setRows] = useState<ISiteAttendanceRow[]>([]);
    const [heading, setHeading] = useState(siteName ?? '');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    // The time typed against each row. Empty means "use the current time".
    const [times, setTimes] = useState<Record<number, string>>({});
    const [savingKey, setSavingKey] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setMessage('');
        try {
            const data = await fetchSiteAttendance(siteId, date);
            setRows(data?.personnel ?? []);
            if (data?.siteName) setHeading(data.siteName);
            if (!data?.personnel?.length) setMessage('Nobody is assigned to this site on that date.');
        } catch {
            // the api layer has already surfaced the reason
            setRows([]);
            setMessage('Could not load the staff for this date.');
        } finally {
            setLoading(false);
        }
    }, [siteId, date]);

    useEffect(() => {
        load();
    }, [load]);

    // Dark backdrop (App.css draws it for body.modal-open) and close on Escape
    useEffect(() => {
        document.body.classList.add('modal-open');
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKey);
        return () => {
            document.body.classList.remove('modal-open');
            window.removeEventListener('keydown', handleKey);
        };
    }, [onClose]);

    const handleClock = async (row: ISiteAttendanceRow, action: SiteAttendanceAction) => {
        const key = `${row.personalID}-${action}`;
        const typed = times[row.personalID]?.trim();

        setSavingKey(key);
        try {
            await clockSitePersonnel({
                siteID: siteId,
                personID: row.personalID,
                date,
                action,
                // No time typed: the server records the current time at the site.
                time: typed ? `${typed}:00` : null,
            });
            setTimes((previous) => ({ ...previous, [row.personalID]: '' }));
            await load();
        } catch {
            // the api layer has already surfaced the reason
        } finally {
            setSavingKey(null);
        }
    };

    return (
        <div
            className="modal fade show editAsignment siteAttendanceModal align-items-xl-center align-items-lg-start overflow-auto align-items-md-center align-items-start justify-content-center"
            id="SiteAttendance"
            aria-labelledby="SiteAttendanceLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog w-100">
                <div className="modal-content p-4">
                    <div className="modal-body p-lg-2 p-md-2 p-0">
                        <div className="formCard mt-0 p-0 border-0">
                            <div className="FormTitleWithdivider position-relative d-flex align-items-center gap-3 mb-3">
                                <h3 className="text-nowrap formTitle m-0">
                                    Clock In / Out{heading ? ` — ${heading}` : ''}
                                </h3>
                                <hr className="w-100" />
                                <button
                                    type="button"
                                    className="siteAttendanceClose"
                                    aria-label="Close"
                                    onClick={onClose}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                                        <path d="M19 5.5L5 19.5M19 19.5L5 5.5" stroke="black" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </div>

                            <div className="siteAttendanceControls">
                                <div className="siteAttendanceDateField">
                                    <label className="form-label mb-2" htmlFor="siteAttendanceDate">Date</label>
                                    <input
                                        type="date"
                                        id="siteAttendanceDate"
                                        className="form-control"
                                        value={date}
                                        max={today()}
                                        onChange={(e) => setDate(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* The table keeps its shape while loading and when empty, so the
                                modal does not collapse and jump between states. */}
                            <div className="siteAttendanceList">
                                <table className="table align-middle m-0">
                                    <thead>
                                        <tr>
                                            <th scope="col">NAME</th>
                                            <th scope="col">POSITION</th>
                                            <th scope="col">SCHEDULED</th>
                                            <th scope="col">IN</th>
                                            <th scope="col">OUT</th>
                                            {canEdit && <th scope="col">TIME</th>}
                                            {canEdit && <th scope="col">ACTION</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading && (
                                            <tr className="siteAttendanceStateRow">
                                                <td colSpan={columnCount}>
                                                    <span className="btnloader loader" />
                                                    <span>Loading staff…</span>
                                                </td>
                                            </tr>
                                        )}

                                        {!loading && rows.length === 0 && (
                                            <tr className="siteAttendanceStateRow">
                                                <td colSpan={columnCount}>
                                                    {message || 'Nobody is assigned to this site on that date.'}
                                                </td>
                                            </tr>
                                        )}

                                        {!loading && rows.map((row) => {
                                            // A recorded time is never overwritten here, so each row
                                            // offers only the one step it is actually missing.
                                            const canClockIn = !row.timeIn;
                                            const canClockOut = !!row.timeIn && !row.timeOut;
                                            const action: SiteAttendanceAction | null =
                                                canClockIn ? 'clockIn' : canClockOut ? 'clockOut' : null;

                                            const busy = savingKey === `${row.personalID}-${action}`;

                                            return (
                                                <tr key={row.personalID}>
                                                    <td
                                                        className="siteAttendanceName"
                                                        title={row.notesDetails || undefined}
                                                    >
                                                        {row.lastName}, {row.firstName}
                                                    </td>
                                                    <td>{row.position || '—'}</td>
                                                    {/* Everyone assigned to the site is listed, so
                                                        somebody not down to work that day says so
                                                        rather than showing an empty range. */}
                                                    <td className="siteAttendanceNowrap">
                                                        {row.scheduledTimeIn || row.scheduledTimeOut ? (
                                                            `${showTime(row.scheduledTimeIn)} – ${showTime(row.scheduledTimeOut)}`
                                                        ) : (
                                                            <span className="siteAttendanceMuted">Not scheduled</span>
                                                        )}
                                                    </td>
                                                    <td className="siteAttendanceNowrap">{showTime(row.timeIn)}</td>
                                                    <td className="siteAttendanceNowrap">{showTime(row.timeOut)}</td>
                                                    {canEdit && (
                                                        <td>
                                                            {action ? (
                                                                <input
                                                                    type="time"
                                                                    className="form-control"
                                                                    aria-label={`Time for ${row.firstName} ${row.lastName}`}
                                                                    value={times[row.personalID] ?? ''}
                                                                    disabled={busy}
                                                                    onChange={(e) =>
                                                                        setTimes((previous) => ({
                                                                            ...previous,
                                                                            [row.personalID]: e.target.value,
                                                                        }))
                                                                    }
                                                                />
                                                            ) : (
                                                                <span className="siteAttendanceMuted">—</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    {canEdit && (
                                                        <td className="siteAttendanceNowrap">
                                                            {action === 'clockIn' && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-white"
                                                                    disabled={busy}
                                                                    onClick={() => handleClock(row, 'clockIn')}
                                                                >
                                                                    {busy ? <span className="btnloader loader" /> : 'Clock In'}
                                                                </button>
                                                            )}
                                                            {action === 'clockOut' && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-primary"
                                                                    disabled={busy}
                                                                    onClick={() => handleClock(row, 'clockOut')}
                                                                >
                                                                    {busy ? <span className="btnloader loader" /> : 'Clock Out'}
                                                                </button>
                                                            )}
                                                            {!action && <span className="siteAttendanceMuted">Complete</span>}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Sits under the TIME and ACTION columns it describes, rather than
                                competing with the date field above. */}
                            {canEdit && (
                                <p className="siteAttendanceHint">
                                    Leave a time empty to record the current time. A time already
                                    recorded cannot be changed here, correct it on the timesheet.
                                </p>
                            )}

                            {/* <div className="d-flex align-items-center justify-content-end gap-3 mt-3">
                                <button type="button" onClick={onClose} className="btn btn-transparent ps-3 pe-4">
                                    Close
                                </button>
                            </div> */}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SiteAttendanceModal;
